<template>
  <div class="auth-wrapper">
    <!-- Elementos decorativos del fondo -->
    <div class="bg-orb bg-orb--1" aria-hidden="true" />
    <div class="bg-orb bg-orb--2" aria-hidden="true" />

    <div class="auth-card" :class="{ 'auth-card--compact': compact }">
      <!-- Marca Nexo (opcional, usada en el login) -->
      <div v-if="showBrand" class="auth-brand">
        <div class="auth-brand__badge">
          <i class="pi pi-robot" aria-hidden="true" />
        </div>
        <h1>Nexo</h1>
        <p>Plataforma de Agentes de IA</p>
      </div>

      <slot />
    </div>

    <p v-if="$slots.footer" class="auth-footer">
      <slot name="footer" />
    </p>
  </div>
</template>

<script setup>
defineProps({
  /** Muestra el encabezado de marca Nexo sobre el contenido */
  showBrand: { type: Boolean, default: false },
  /** Tarjeta más angosta para estados simples (spinners, mensajes) */
  compact: { type: Boolean, default: false },
});
</script>

<style scoped>
.auth-wrapper {
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.25rem;
  padding: 2rem 1rem;
  overflow: hidden;
  background:
    radial-gradient(ellipse at top left, rgba(255, 255, 255, 0.14), transparent 50%),
    linear-gradient(135deg, #4f46e5 0%, #7c3aed 55%, #6d28d9 100%);
}

/* Orbes decorativos difuminados */
.bg-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(70px);
  opacity: 0.35;
  pointer-events: none;
}

.bg-orb--1 {
  width: 420px;
  height: 420px;
  top: -120px;
  left: -120px;
  background: #a5b4fc;
}

.bg-orb--2 {
  width: 380px;
  height: 380px;
  bottom: -140px;
  right: -100px;
  background: #f0abfc;
}

.auth-card {
  position: relative;
  z-index: 1;
  background: white;
  border-radius: 16px;
  padding: 2.5rem;
  width: 100%;
  max-width: 430px;
  box-shadow: 0 24px 64px rgba(17, 24, 39, 0.25);
  animation: card-enter 0.45s ease-out;
}

.auth-card--compact {
  max-width: 360px;
  text-align: center;
}

@keyframes card-enter {
  from {
    opacity: 0;
    transform: translateY(16px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.auth-brand {
  text-align: center;
  margin-bottom: 1.75rem;
}

.auth-brand__badge {
  width: 56px;
  height: 56px;
  margin: 0 auto 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  font-size: 1.6rem;
  box-shadow: 0 8px 20px rgba(99, 102, 241, 0.35);
}

.auth-brand h1 {
  font-size: 1.9rem;
  font-weight: 800;
  color: #1e293b;
  margin: 0 0 0.2rem;
  letter-spacing: -0.02em;
}

.auth-brand p {
  color: #6b7280;
  font-size: 0.9rem;
  margin: 0;
}

.auth-footer {
  position: relative;
  z-index: 1;
  margin: 0;
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.82rem;
}

.auth-footer :deep(a) {
  color: white;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.auth-footer :deep(a:hover) {
  color: #e0e7ff;
}

/* Móviles: tarjeta a ancho completo con padding reducido */
@media (max-width: 480px) {
  .auth-card {
    padding: 1.75rem 1.5rem;
    border-radius: 14px;
  }

  .auth-brand__badge {
    width: 48px;
    height: 48px;
    font-size: 1.35rem;
  }

  .auth-brand h1 {
    font-size: 1.6rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .auth-card {
    animation: none;
  }
}
</style>
